// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Config } from "../config";
import {
    DocumentItem,
    InventoryDetail,
    InventoryOrder,
    InventoryOrderDetail,
    ReportOptionsResponse,
    VerifyFacilitatorRequest,
    VerifyFacilitatorResult,
    DocumentUploadResponse,
    UpdateDocumentRequest,
    BatchScoringRequest,
    BatchScoringRequestResult,
    AssessmentAdminGroup,
    AssessmentAdminGroupDetail,
    AssessmentAdmin,
    CreateGroupRequest,
    UpdateGroupRequest,
    AddGroupMemberRequest,
    AddGroupMemberResponse,
    AssessmentQualification,
} from "./types/assessmentAPITypes";
import { ScoringSnapshotResponse } from "./types/scoringStatusTypes";

const apiManagementRootUrl: string | undefined = Config.REACT_APP_CCL_API_MANAGEMENT_URL;

// Define a service using a base URL and expected endpoints
export const cclTokenedAssessmentApi = createApi({
    reducerPath: "cclTokenedAssessmentApi",
    tagTypes: [
        "Inventory",
        "ActiveDocuments",
        "AllDocuments",
        "SharedAdminGroupDetails",
        "SharedGroupAdmins",
    ],
    baseQuery: fetchBaseQuery({
        baseUrl: apiManagementRootUrl,
        prepareHeaders: (headers, { getState }) => {
            const token = localStorage.getItem("token") as string;
            headers.set("authorization", `Bearer ${token}`);
            return headers;
        },
    }),
    endpoints: (builder) => ({
        getSessionsInventory: builder.query<InventoryDetail, number>({
            query: (esKey) => `ccl-access2-assessments/v1.0/inventory/session/${esKey}`,
            providesTags: (_, __, esKey) => [{ type: "Inventory", id: esKey }],
        }),
        getAdminsInventory: builder.query<InventoryDetail[], number>({
            query: (imKey) => `ccl-access2-assessments/v1.0/inventory/admin/${imKey}`,
        }),
        getGroupInventory: builder.query<InventoryDetail[], number>({
            query: (groupKey) => `ccl-access2-assessments/v1.0/inventory/group/${groupKey}`,
        }),
        getGroupOrders: builder.query<InventoryOrder[], number>({
            query: (groupKey) =>
                `ccl-access2-assessments/v1.0/inventory/orders/by-group/${groupKey}`,
        }),
        getOrderDetails: builder.query<
            InventoryOrderDetail[],
            { groupKey: number; orderId: string }
        >({
            query: ({ groupKey, orderId }) =>
                `ccl-access2-assessments/v1.0/inventory/order/${groupKey}/${orderId}`,
        }),
        getReportOptions: builder.query<
            ReportOptionsResponse,
            { instrumentId: number; participantId: number }
        >({
            query: ({ instrumentId, participantId }) => ({
                url: `ccl-access2-assessments/v1.0/assessments/report-options/${instrumentId}?participantId=${participantId}`,
            }),
        }),
        requestScoring: builder.mutation<BatchScoringRequestResult, BatchScoringRequest>({
            query: (scoringRequest) => ({
                url: `ccl-access2-assessments/v1.0/assessments/request-scoring`,
                method: "POST",
                body: scoringRequest,
            }),
        }),
        verifyFacilitator: builder.mutation<VerifyFacilitatorResult, VerifyFacilitatorRequest>({
            query: (request) => ({
                url: `ccl-access2-assessments/v1.0/assessment-admin/facilitator/verify`,
                method: "POST",
                body: request,
            }),
        }),
        scoringSnapshotBySession: builder.query<ScoringSnapshotResponse, string>({
            query: (esKey) =>
                `ccl-access2-assessments/v1.0/assessments/scoring-history/by-session/${esKey}`,
        }),
        getActiveDocuments: builder.query<DocumentItem[], void>({
            query: () => `ccl-access2-assessments/v1.0/documents/active`,
            providesTags: ["ActiveDocuments"],
        }),
        getAllDocuments: builder.query<DocumentItem[], void>({
            query: () => `ccl-access2-assessments/v1.0/documents/get-all`,
            providesTags: ["AllDocuments"],
        }),
        enableDocument: builder.mutation<DocumentItem, number>({
            query: (documentId) => ({
                url: `ccl-access2-assessments/v1.0/documents/enable/${documentId}`,
                method: "PUT",
            }),
            invalidatesTags: ["ActiveDocuments", "AllDocuments"],
        }),
        disableDocument: builder.mutation<DocumentItem, number>({
            query: (documentId) => ({
                url: `ccl-access2-assessments/v1.0/documents/disable/${documentId}`,
                method: "PUT",
            }),
            invalidatesTags: ["ActiveDocuments", "AllDocuments"],
        }),
        uploadDocument: builder.mutation<DocumentUploadResponse, FormData>({
            query: (formData) => ({
                url: `ccl-access2-assessments/v1.0/documents/upload`,
                method: "POST",
                body: formData,
            }),
            invalidatesTags: ["ActiveDocuments", "AllDocuments"],
        }),
        updateDocument: builder.mutation<DocumentItem, UpdateDocumentRequest>({
            query: (request) => ({
                url: `ccl-access2-assessments/v1.0/documents/${request.documentId}`,
                method: "PUT",
                body: request.body,
            }),
            invalidatesTags: ["ActiveDocuments", "AllDocuments"],
        }),
        deleteDocument: builder.mutation<void, number>({
            query: (documentId) => ({
                url: `ccl-access2-assessments/v1.0/documents/${documentId}`,
                method: "DELETE",
            }),
            invalidatesTags: ["ActiveDocuments", "AllDocuments"],
        }),
        getAssessmentAdminGroupDetailByImKey: builder.query<AssessmentAdminGroupDetail, number>({
            query: (imKey) =>
                `ccl-access2-assessments/v1.0/assessment-admin/individual/detail/${imKey}`,
        }),
        getSharedAdminGroups: builder.query<AssessmentAdminGroup[], void>({
            query: () => `ccl-access2-assessments/v1.0/assessment-admin/groups/shared`,
        }),
        getAssessmentAdminGroupByEmail: builder.query<AssessmentAdminGroup[], string>({
            query: (email) =>
                `ccl-access2-assessments/v1.0/assessment-admin/individual/by-email?email=${encodeURIComponent(email)}`,
        }),
        getAssessmentQualificationsByEmail: builder.query<AssessmentQualification[], string>({
            query: (email) =>
                `ccl-access2-assessments/v1.0/assessment-admin/facilitator/qualifications?email=${encodeURIComponent(email)}`,
        }),
        getSharedAdminGroupDetails: builder.query<AssessmentAdminGroupDetail[], void>({
            query: () => `ccl-access2-assessments/v1.0/assessment-admin/groups/detail/shared`,
            providesTags: ["SharedAdminGroupDetails"],
        }),
        getSharedGroupAdmins: builder.query<AssessmentAdmin[], void>({
            query: () => `ccl-access2-assessments/v1.0/assessment-admin/groups/shared/admins`,
            providesTags: ["SharedGroupAdmins"],
        }),
        createSharedGroup: builder.mutation<AssessmentAdminGroupDetail, CreateGroupRequest>({
            query: (body) => ({
                url: `ccl-access2-assessments/v1.0/assessment-admin/groups`,
                method: "POST",
                body,
            }),
            invalidatesTags: ["SharedAdminGroupDetails"],
        }),
        updateSharedGroup: builder.mutation<AssessmentAdminGroupDetail, UpdateGroupRequest>({
            query: (body) => ({
                url: `ccl-access2-assessments/v1.0/assessment-admin/groups/update`,
                method: "POST",
                body,
            }),
            invalidatesTags: ["SharedAdminGroupDetails"],
        }),
        addGroupMember: builder.mutation<AddGroupMemberResponse, AddGroupMemberRequest>({
            query: (body) => ({
                url: `ccl-access2-assessments/v1.0/assessment-admin/groups/add-member`,
                method: "POST",
                body,
            }),
            invalidatesTags: ["SharedAdminGroupDetails", "SharedGroupAdmins"],
        }),
        scoringSnapshotByParticipant: builder.query<ScoringSnapshotResponse, number>({
            query: (esiKey) =>
                `ccl-access2-assessments/v1.0/assessments/scoring-history/by-participant/${esiKey}`,
        }),
        scoringSnapshotByDateRange: builder.mutation<
            ScoringSnapshotResponse,
            { startDate: string; endDate: string }
        >({
            query: (body) => ({
                url: `ccl-access2-assessments/v1.0/assessments/scoring-history/by-date-range`,
                method: "POST",
                body,
            }),
        }),
    }),
});

export const {
    useGetSessionsInventoryQuery,
    useGetAdminsInventoryQuery,
    useGetGroupInventoryQuery,
    useGetGroupOrdersQuery,
    useGetOrderDetailsQuery,
    useLazyGetReportOptionsQuery,
    useRequestScoringMutation,
    useVerifyFacilitatorMutation,
    useScoringSnapshotBySessionQuery,
    useLazyScoringSnapshotBySessionQuery,
    useGetActiveDocumentsQuery,
    useGetAllDocumentsQuery,
    useEnableDocumentMutation,
    useDisableDocumentMutation,
    useUploadDocumentMutation,
    useUpdateDocumentMutation,
    useDeleteDocumentMutation,
    useGetAssessmentAdminGroupDetailByImKeyQuery,
    useGetSharedAdminGroupsQuery,
    useLazyGetSharedAdminGroupsQuery,
    useLazyGetAssessmentAdminGroupByEmailQuery,
    useLazyGetAssessmentQualificationsByEmailQuery,
    useGetSharedAdminGroupDetailsQuery,
    useLazyGetSharedAdminGroupDetailsQuery,
    useGetSharedGroupAdminsQuery,
    useLazyGetSharedGroupAdminsQuery,
    useCreateSharedGroupMutation,
    useUpdateSharedGroupMutation,
    useAddGroupMemberMutation,
    useScoringSnapshotByParticipantQuery,
    useLazyScoringSnapshotByParticipantQuery,
    useScoringSnapshotByDateRangeMutation,
} = cclTokenedAssessmentApi;
